import React, { useEffect } from 'react';
import { useSelfSignUpFeedbackSubscription } from '~/libs/gql/subscriptions/self_sign_up/selfSignUpFeedback.generated';
import { progressStepManagerOptions } from './options';

type SelfSignUpFeedbackPayload = {
  selfSignUpFeedback?: {
    event?: (typeof progressStepManagerOptions.steps)[number]['event'];
    message?: string | null;
    success?: boolean;
  } | null;
};

type SelfSignUpFeedbackOnData = {
  data?: {
    data?: SelfSignUpFeedbackPayload;
  };
};

export const useProgressStepManager = (onComplete: () => void, hasError: boolean) => {
  const [stepManager, setStepManager] = React.useState(progressStepManagerOptions.initialStepManager);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const orderedEvents = React.useMemo(
    () => progressStepManagerOptions.steps.map(step => step.event),
    [],
  );

  useSelfSignUpFeedbackSubscription({
    onData: ({ data }: SelfSignUpFeedbackOnData) => {
      const feedback = data?.data?.selfSignUpFeedback;
      const event = feedback?.event;
      const success = Boolean(feedback?.success);
      const message = feedback?.message || null;

      if (event && success !== undefined) {
        setErrorMessage(success ? null : message);

        setStepManager((prev) => {
          const next = { ...prev };

          if (success) {
            const eventIndex = orderedEvents.indexOf(event);
            if (eventIndex !== -1) {
              orderedEvents.slice(0, eventIndex + 1).forEach((e) => {
                next[e] = 'completed';
              });
            }
          } else {
            next[event] = 'error';
          }

          return next;
        });
      }
    },
  });

  const resetStepManager = React.useCallback(() => {
    setStepManager(progressStepManagerOptions.initialStepManager);
    setErrorMessage(null);
  }, [setStepManager]);

  useEffect(() => {
    if (hasError) return;
    if (Object.values(stepManager).every(status => status === 'completed')) {
      onComplete();
    }
  }, [hasError, onComplete, stepManager]);

  useEffect(() => {
    if (hasError) {
      setErrorMessage(null);
      setStepManager({
        firing_up_your_order: 'error',
        prepping_your_profile: 'error',
        seasoning_your_design: 'error',
        serving_up_your_my_pop_dashboard: 'error',
      });
    }
  }, [hasError, setStepManager]);

  return { errorMessage, resetStepManager, stepManager };
};
